import palette from './palette.json';

/*
 * The theme preference, outside React.
 *
 * index.js calls applyTheme() before the first render, so a returning user never
 * sees one frame of the wrong theme. ThemeContext keeps it in sync afterwards and
 * stores it on the server (PUT /users/me/preferences) so it follows the person
 * from one device to the next.
 *
 * A theme is purely how the app looks: it grants nothing, and no backend route
 * reads it for anything but echoing it back.
 */
export const THEMES = Object.keys(palette.themes);          // lab_light, field_dark, botanical
export const PREFERENCES = ['system', ...THEMES];
// Not 'system': an operating system set to dark must not change the app for
// someone who never asked. Choosing "match system" is one click away.
export const DEFAULT_PREFERENCE = 'lab_light';
export const STORAGE_KEY = 'mb_theme';

export function isPreference(value) {
  return PREFERENCES.includes(value);
}

export function resolveTheme(preference, prefersDark) {
  if (THEMES.includes(preference)) return preference;
  if (preference === 'system') return prefersDark ? 'field_dark' : 'lab_light';
  return DEFAULT_PREFERENCE;
}

export function systemPrefersDark() {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  } catch {
    return false;
  }
}

// Storage can be unavailable (private windows, blocked site data): the app then
// simply starts in the default theme every time.
export function readStoredPreference() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isPreference(value) ? value : DEFAULT_PREFERENCE;
  } catch {
    return DEFAULT_PREFERENCE;
  }
}

export function storePreference(preference) {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // not persisted; the choice still applies for this session
  }
}

export function applyTheme(preference, doc = document) {
  const theme = resolveTheme(preference, systemPrefersDark());
  doc.documentElement.setAttribute('data-theme', theme);
  const meta = doc.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', palette.themes[theme].meta);
  return theme;
}

const tokenValue = (theme, name) => {
  const token = palette.tokens.find(t => t.name === name);
  return theme === 'lab_light' ? token.hex : token[theme];
};

// The colours a picker shows for each theme, read from the same palette the CSS
// is generated from, so a preview can never disagree with the theme it names.
export function swatches(theme) {
  return {
    page: tokenValue(theme, 'page'),
    surface: tokenValue(theme, 'surface'),
    primary: tokenValue(theme, 'primary'),
    text: theme === 'lab_light'
      ? palette.tokens.find(t => t.name === 'text').hex
      : (palette.tokens.find(t => t.name === 'text')[`${theme}_fg`] || tokenValue(theme, 'text')),
  };
}
